import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";

import LeftSidebar from "./components/Layout/LeftSidebar";
import Footer from "./components/Layout/Footer";
import TopNavbar from "./components/Layout/TopNavbar"; 
import ScrollToTop from "./components/Layout/ScrollToTop";

// ByBehnam
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./routes/ProtectedRoute";

import AdminDashboard from "./pages/admin/Dashboard"
import AdminUsers from "./pages/admin/Users"
import AdminStoresPage from "./pages/admin/Stores"
import AdminCategoriesPage from "./pages/admin/Categories"
import AdminMerchantCategoriesPage from "./pages/admin/MerchantCategories"
import AdminTransactionsPage from "./pages/admin/Transactions"
import AdminPurchaseAccountsPage from "./pages/admin/PurchaseAccounts"
import AdminTransactionsImportPage from "./pages/admin/TransactionsImport"
import AdminRequestsImportPage from "./pages/admin/RequestsImport";
import AdminCreditRequests from "./pages/admin/CreditRequests";
import AdminNotifications from "./pages/admin/Notifications";
import AdminProductCategoriesPage from "./pages/admin/ProductCategories"
import AdminProductsPage from "./pages/admin/Products";

import StoreDashboard from "./pages/store/Dashboard"
import StoreProfile from "./pages/store/Profile";
import StoreWallet from "./pages/store/Wallet";
import StoreCreditRequests from "./pages/store/CreditRequests";
import StoreTransactionsPage from "./pages/store/Transactions"
import StorePurchaseAccountsPage from "./pages/store/PurchaseAccounts"
import StoreOrders from "./pages/store/Orders";
import StoreOrderDetails from "./pages/store/OrderDetails";
import StorePendingPayments from "./pages/store/PendingPayments";

import UserDashboard from "./pages/user/Dashboard"
import UserStores from "./pages/user/Stores";
import UserCreditRequests from "./pages/user/CreditRequests";
import UserTransactionsPage from "./pages/user/Transactions"
import UserPurchaseAccountsPage from "./pages/user/PurchaseAccounts"
import UserStoreProducts from "./pages/user/StoreProducts";
import UserCart from "./pages/user/Cart";
import UserOrderCheckout from "./pages/user/OrderCheckout";
import UserOrderPayment from "./pages/user/OrderPayment";


// Front Pages
import Home from "./pages/Home";
import Features from "./pages/Features";
import Team from "./pages/Team";
import Faq from "./pages/Faq";
import Contact from "./pages/Contact";

// Dashboard
import Ecommerce from "./pages/dashboard/Ecommerce";

// eCommerce
import ProductsGrid from "./pages/ecommerce/ProductsGrid";
import ProductsList from "./pages/ecommerce/ProductsList";
import ProductsDetails from "./pages/ecommerce/ProductsDetails";
import CreateProduct from "./pages/ecommerce/CreateProduct";
import EditProduct from "./pages/ecommerce/EditProduct";
import Cart from "./pages/ecommerce/Cart";
import Checkout from "./pages/ecommerce/Checkout";
import Orders from "./pages/ecommerce/Orders";
import OrderDetails from "./pages/ecommerce/Orders/OrderDetails";
import CreateOrder from "./pages/ecommerce/Orders/CreateOrder";
import Tracking from "./pages/ecommerce/Orders/Tracking";
import Customers from "./pages/ecommerce/Customers";
import CustomersDetails from "./pages/ecommerce/Customers/CustomersDetails";
import Categories from "./pages/ecommerce/Categories";
import Sellers from "./pages/ecommerce/Sellers";
import SellersDetails from "./pages/ecommerce/Sellers/SellersDetails";
import CreateSeller from "./pages/ecommerce/Sellers/CreateSeller";
import Reviews from "./pages/ecommerce/Reviews";
import Refunds from "./pages/ecommerce/Refunds";

// Restaurant
import Restaurant from "./pages/dashboard/Restaurant";
import Menus from "./pages/restaurant/Menus";
import DishDetails from "./pages/restaurant/DishDetails";

// Invoices
import Invoices from "./pages/invoices";
import InvoicesDetails from "./pages/invoices/InvoicesDetails";
import CreateInvoice from "./pages/invoices/CreateInvoice";
import EditInvoice from "./pages/invoices/EditInvoice";

// Users
import Users from "./pages/users";
import UsersList from "./pages/users/UsersList";
import AddUsers from "./pages/users/AddUsers";

// Profile
import UserProfile from "./pages/profile/UserProfile";
import TeamsProfile from "./pages/profile/TeamsProfile";
import MyProjects from "./pages/profile/MyProjects";

// Authentication
import SignIn from "./pages/authentication/SignIn";
import SignUp from "./pages/authentication/SignUp";
import ForgotPassword from "./pages/authentication/ForgotPassword";
import ResetPassword from "./pages/authentication/ResetPassword";
import ConfirmEmail from "./pages/authentication/ConfirmEmail";
import LockScreen from "./pages/authentication/LockScreen";
import Logout from "./pages/authentication/Logout";

// Extra Pages
import Search from "./pages/Search";
import BlankPage from "./pages/BlankPage";
import NotFound from "./pages/NotFound";
import InternalError from "./pages/InternalError";

import Widgets from "./pages/Widgets";
import Maps from "./pages/Maps";
import Notifications from "./pages/Notifications";
import Members from "./pages/Members";
import MyProfile from "./pages/MyProfile";

// Settings
import AccountSettings from "./pages/settings/AccountSettings";
import ChangePassword from "./pages/settings/ChangePassword";
import Connections from "./pages/settings/Connections";
import PrivacyPolicy from "./pages/settings/PrivacyPolicy";
import TermsConditions from "./pages/settings/TermsConditions";

import Starter from "./pages/Starter";

const App = () => {
  const [active, setActive] = useState(false);
  const toggleActive = () => setActive(!active);

  return (
    <>
      <div className={`main-wrapper-content ${active ? "active" : ""}`}>
        <Router>
          <AppLayout active={active} toggleActive={toggleActive} />
        </Router>
      </div>
    </>
  );
};

const AppLayout = ({ active, toggleActive }) => {
  const location = useLocation();
  const pathname = location.pathname;

  const isAuthPage = [
    "/authentication/sign-in",
    "/authentication/sign-up",
    "/authentication/forgot-password",
    "/authentication/reset-password",
    "/authentication/confirm-email",
    "/authentication/lock-screen",
    "/authentication/logout",
    "/not-found",
    "/",
    "/features",
    "/team",
    "/faq",
    "/contact",
    "/ecommerce",
  ].includes(pathname);

  return (
    <>
    <AuthProvider>
      {!isAuthPage && <LeftSidebar toggleActive={toggleActive} />}
      <div className="main-content d-flex flex-column">
        <ScrollToTop />
        {!isAuthPage && <TopNavbar toggleActive={toggleActive} />}

        <Routes>
          {/* --- Protected Routes --- */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stores"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminStoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminCategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/merchant/categories"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminMerchantCategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/credit-requests"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminCreditRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/transactions"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminTransactionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/purchase-accounts"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminPurchaseAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/transactions/import"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminTransactionsImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/requests/import"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminRequestsImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminNotifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/product/categories"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminProductCategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <AdminProductsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/store/dashboard"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/profile"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/wallet"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreWallet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/credit-requests"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreCreditRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/transactions"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreTransactionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/purchase-accounts"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StorePurchaseAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/orders"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/store/orders/:orderCode"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StoreOrderDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/store/pending-payments"
            element={
              <ProtectedRoute allowedRoles={["STORE"]}>
                <StorePendingPayments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user/dashboard"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/stores"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserStores />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/stores/:storeCode/products"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserStoreProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/stores/:storeCode/cart"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserCart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/stores/:storeCode/checkout"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserOrderCheckout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/orders/:orderCode/payment"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserOrderPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/credit-requests"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserCreditRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/transactions"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserTransactionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user/purchase-accounts"
            element={
              <ProtectedRoute allowedRoles={["USER"]}>
                <UserPurchaseAccountsPage />
              </ProtectedRoute>
            }
          />

          {/* --- Front Pages --- */}
          <Route path="/" element={<Home />} />
          {/*<Route path="/features" element={<Features />} />
          <Route path="/team" element={<Team />} />*/ }
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact" element={<Contact />} />


          {/* --- Notification --- */}
          <Route path="/notifications" element={<Notifications />} />


          {/* --- Ecommerce --- */}
          <Route path="/ecommerce" element={<Ecommerce />} />

          <Route path="/products-grid" element={<ProductsGrid />} />
          <Route path="/products-list" element={<ProductsList />} />
          <Route path="/products-details" element={<ProductsDetails />} />
          <Route path="/create-product" element={<CreateProduct />} />
          <Route path="/edit-product" element={<EditProduct />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders-details" element={<OrderDetails />} />
          <Route path="/create-details" element={<CreateOrder />} />
          <Route path="/tracking" element={<Tracking />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/customers-details" element={<CustomersDetails />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/sellers" element={<Sellers />} />
          <Route path="/sellers-details" element={<SellersDetails />} />
          <Route path="/create-seller" element={<CreateSeller />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/refunds" element={<Refunds />} />

          {/* --- Restaurant --- */}
          <Route path="/dashboard/restaurant" element={<Restaurant />} />
          <Route path="/restaurant/menus" element={<Menus />} />
          <Route path="/restaurant/dish-details" element={<DishDetails />} />

          {/* Invoices */}
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/details" element={<InvoicesDetails />} />
          <Route path="/invoices/create" element={<CreateInvoice />} />
          <Route path="/invoices/edit" element={<EditInvoice />} />

          {/* Users */}
          <Route path="/users" element={<Users />} />
          <Route path="/users/users-list" element={<UsersList />} />
          <Route path="/users/add-user" element={<AddUsers />} />

          {/* Profile */}
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/profile/teams" element={<TeamsProfile />} />
          <Route path="/profile/projects" element={<MyProjects />} />

          <Route path="/starter" element={<Starter />} />



          {/* --- Authentication --- */}
          <Route path="/authentication/sign-in" element={<SignIn />} />
          <Route path="/authentication/sign-up" element={<SignUp />} />
          <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
          <Route path="/authentication/reset-password" element={<ResetPassword />} />
          <Route path="/authentication/confirm-email" element={<ConfirmEmail />} />
          <Route path="/authentication/lock-screen" element={<LockScreen />} />
          <Route path="/authentication/logout" element={<Logout />} />
          
          {/* Extra Pages */}
          <Route path="/blank-page" element={<BlankPage />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="/internal-error" element={<InternalError />} />
          <Route path="/members" element={<Members />} />
          <Route path="/search" element={<Search />} />

          {/* Settings */}
          <Route path="/settings/account-settings" element={<AccountSettings />} />
          <Route path="/settings/change-password" element={<ChangePassword />} />
          <Route path="/settings/connections" element={<Connections />} />
          <Route path="/settings/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/settings/terms-conditions" element={<TermsConditions />} />

          {/* سایر مسیرها... */}
        </Routes>

        {!isAuthPage && <Footer />}
      </div>
    </AuthProvider>
    </>
  );
};

export default App;
